import React, { useState, useEffect } from 'react';
import storeService from '../services/storeService';
import { useStore } from '../context/StoreContext';
import { useAuth } from '../context/AuthContext';
import cfdService from '../services/cfdService';

const TerminalSetupPage = () => {
  const [tills, setTills] = useState([]);
  const { tillId, getCurrentTillForStore, setCurrentTillForStore } = useAuth();
  const [selectedTillId, setSelectedTillId] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const { activeStore } = useStore();

  useEffect(() => {
    // Fetch all available tills for the active store
    if (activeStore && activeStore._id !== 'setup') {
      storeService.getStoreById(activeStore._id).then(data => {
        const nextTills = data.tills || [];
        setTills(nextTills);

        // Safe migration path: only migrate legacy till assignment if it exists in this store.
        const scopedTillId = getCurrentTillForStore(activeStore._id);
        const canMigrateLegacy = !scopedTillId && tillId && nextTills.some((till) => till._id === tillId);
        if (canMigrateLegacy) {
          setCurrentTillForStore(activeStore._id, tillId);
        }
        setIsLoading(false);
      });
    }
  }, [activeStore, getCurrentTillForStore, setCurrentTillForStore, tillId]);

  useEffect(() => {
    if (!activeStore?._id || activeStore._id === 'setup') {
      setSelectedTillId('');
      return;
    }
    const scopedTillId = getCurrentTillForStore(activeStore._id) || '';
    setSelectedTillId(scopedTillId);
  }, [activeStore, getCurrentTillForStore]);

  const handleSave = async () => {
    if (!selectedTillId) return;
    setError('');
    setSuccess('');
    setIsSaving(true);
    try {
      setCurrentTillForStore(activeStore?._id, selectedTillId);
      const { token } = await cfdService.createSession(selectedTillId);
      if (token) {
        localStorage.setItem('cfdSessionToken', token);
      }
      const selectedTill = tills.find(t => t._id === selectedTillId);
      setSuccess(`This terminal has been successfully configured as "${selectedTill?.name || 'Unknown'}".`);
    } catch (err) {
      const message = err.response?.data?.message || 'Failed to configure terminal. Please try again.';
      setError(message);
    } finally {
      setIsSaving(false);
    }
  };

  if (!activeStore) {
    return <div className="p-6">Please select an active store first.</div>;
  }

  if (isLoading) return <div>Loading available devices...</div>;

  return (
    <div className="p-6 bg-gray-100 min-h-screen flex justify-center items-center">
      <div className="w-full max-w-lg bg-white p-8 rounded-lg shadow-md">
        <h1 className="text-2xl font-bold text-gray-800 mb-2">Terminal Setup</h1>
        <p className="text-gray-600 mb-6">Select which POS device this terminal represents for the store: <strong>{activeStore.name}</strong></p>
        
        {error && <p className="bg-red-100 text-red-700 p-3 rounded mb-4">{error}</p>}
        {success && <p className="bg-green-100 text-green-700 p-3 rounded mb-4">{success}</p>}

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">Select Device (Till)</label>
            <select 
              value={selectedTillId} 
              onChange={(e) => setSelectedTillId(e.target.value)}
              className="mt-1 p-2 border rounded-md w-full"
            >
              <option value="">-- Not Configured --</option>
              {tills.map(till => (
                <option key={till._id} value={till._id}>{till.name}</option>
              ))}
            </select>
          </div>
          <div className="flex justify-end pt-4">
            <button onClick={handleSave} disabled={!selectedTillId || isSaving} className="bg-primary text-white px-6 py-2 rounded-md hover:bg-primary-dark disabled:opacity-50">
              {isSaving ? 'Saving...' : 'Save Configuration for this Terminal'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TerminalSetupPage;
